import { Paperclip } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { AttachmentImage } from '../../ui/AttachmentImage';
import { SmartLink } from '../../ui/SmartLink';

/**
 * CLE-182 — dark-theme markdown renderer for the Task Detail page.
 *
 * Dependency-free and XSS-safe: everything is emitted through React text nodes
 * (no `dangerouslySetInnerHTML`). Supports the block + inline subset the rich
 * editor produces — headings, bold/italic, inline code, fenced code blocks,
 * unordered/ordered lists, links, inline images, and blank-line paragraphs.
 */

// A bare URL pasted straight into the text, with no markdown around it. It sits
// after the `[text](url)` and `![alt](url)` alternatives so those still win when
// both could match at the same position — alternation is ordered, leftmost-first.
//
// The last character may not be punctuation, so a sentence-ending "." or the ")"
// of "(vezi https://exemplu.ro)" stays in the prose instead of being swallowed
// into a broken link.
const BARE_URL = /(?:https?:\/\/|www\.)[^\s<>"']*[^\s<>"'.,;:!?)\]}]/;

const INLINE = new RegExp(
  `(\\*\\*[^*]+\\*\\*|\\*[^*]+\\*|\`[^\`]+\`|!\\[[^\\]]*\\]\\([^)]+\\)|\\[[^\\]]+\\]\\([^)]+\\)|${BARE_URL.source}|@[\\w.-]+)`,
  'g',
);

// Only http(s) and same-origin/relative links are allowed (blocks javascript:).
function safeHref(href: string): string {
  return /^(https?:\/\/|\/)/i.test(href) ? href : '#';
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(INLINE);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (!part) return null;

    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          key={key}
          className="rounded bg-gray-300 px-1 py-0.5 font-mono text-[0.85em] text-gray-800"
        >
          {part.slice(1, -1)}
        </code>
      );
    }

    // Inline image: ![alt](url). Photos pasted into a comment land here rather
    // than in the attachment chips, so they need the same shimmer — a comment
    // that is only a picture would otherwise be a blank space while it loads.
    const img = /^!\[([^\]]*)\]\(([^)]+)\)$/.exec(part);
    if (img) {
      const alt = img[1];
      return (
        <AttachmentImage
          key={key}
          src={safeHref(img[2])}
          alt={alt}
          className="my-2 max-h-96 max-w-full rounded-md border border-gray-200"
          placeholderClassName="h-48 w-72"
          fallback={
            <span className="my-2 inline-flex items-center gap-1.5 rounded border border-gray-200 px-2 py-1 text-[12px] text-gray-500">
              <Paperclip size={12} /> {alt || 'Imagine indisponibilă'}
            </span>
          }
        />
      );
    }

    // Link: [text](url)
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      return (
        <SmartLink
          key={key}
          href={safeHref(link[2])}
          className="text-brand-700 underline decoration-brand-700/40 hover:decoration-brand-700"
        >
          {link[1]}
        </SmartLink>
      );
    }

    // Bare URL: nobody wraps links in markdown when typing a comment, so make
    // what they pasted clickable. `www.` gets a scheme or the browser would
    // resolve it as a path relative to the app.
    if (new RegExp(`^${BARE_URL.source}$`).test(part)) {
      return (
        <SmartLink
          key={key}
          href={safeHref(/^www\./i.test(part) ? `https://${part}` : part)}
          // `break-all`: a pasted URL is one unbreakable word and would push the
          // comment wider than the phone screen.
          className="break-all text-brand-700 underline decoration-brand-700/40 hover:decoration-brand-700"
        >
          {part}
        </SmartLink>
      );
    }

    if (part.startsWith('@')) {
      return (
        <span key={key} className="font-medium text-brand-700">
          {part}
        </span>
      );
    }

    return <Fragment key={key}>{part}</Fragment>;
  });
}

const LIST_LINE = /^\s*(?:[-*]|\d+\.)\s+/;
const ORDERED_LINE = /^\s*\d+\.\s+/;

interface ListItem {
  text: string;
  children: ListNode[];
}

interface ListNode {
  ordered: boolean;
  /** For a numbered list, the number the author gave its first item (1 unless written otherwise). */
  start: number;
  items: ListItem[];
}

/** The number written on a numbered line ("3. …" → 3), 1 when it is not a numbered line. */
function listStart(line: string | undefined): number {
  const n = /^\s*(\d+)\.\s+/.exec(line ?? '')?.[1];
  return n ? Math.max(1, Number(n)) : 1;
}

/**
 * Raw list lines → a tree, nesting by indentation: every two spaces (or a
 * tab) is one level deeper. Before this the lines were flattened, so a task
 * description with sub-points showed them as siblings of their parent — the
 * structure the author typed was gone outside the editor (AIWS-095).
 */
function parseList(lines: string[]): ListNode {
  const depthOf = (line: string) => {
    const ws = /^\s*/.exec(line)?.[0] ?? '';
    return Math.floor(ws.replace(/\t/g, '  ').length / 2);
  };
  const root: ListNode = {
    ordered: ORDERED_LINE.test(lines[0] ?? ''),
    start: listStart(lines[0]),
    items: [],
  };
  const stack: Array<{ depth: number; node: ListNode }> = [
    { depth: depthOf(lines[0] ?? ''), node: root },
  ];

  for (const line of lines) {
    const depth = depthOf(line);
    const ordered = ORDERED_LINE.test(line);
    const text = line.replace(LIST_LINE, '');
    while (stack.length > 1 && depth < stack[stack.length - 1].depth) stack.pop();
    let top = stack[stack.length - 1];
    if (depth > top.depth && top.node.items.length > 0) {
      const child: ListNode = { ordered, start: listStart(line), items: [] };
      top.node.items[top.node.items.length - 1].children.push(child);
      stack.push({ depth, node: child });
      top = stack[stack.length - 1];
    }
    top.node.items.push({ text, children: [] });
  }
  return root;
}

function renderList(node: ListNode, key: string, nested = false): ReactNode {
  const Tag = node.ordered ? 'ol' : 'ul';
  const marker = node.ordered ? 'list-decimal' : nested ? 'list-[circle]' : 'list-disc';
  return (
    <Tag
      key={key}
      // The browser numbers the items; `start` keeps the author's first number.
      start={node.ordered && node.start !== 1 ? node.start : undefined}
      className={`${nested ? 'mt-1' : 'my-2'} ${marker} space-y-1 pl-5`}
    >
      {node.items.map((item, i) => (
        <li key={`${key}-${i}`}>
          {renderInline(item.text, `${key}-${i}`)}
          {item.children.map((child, c) => renderList(child, `${key}-${i}-${c}`, true))}
        </li>
      ))}
    </Tag>
  );
}

interface Block {
  type: 'h1' | 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'code';
  lines: string[];
}

// Group raw lines into block-level chunks (headings, lists, code fences, paras).
function toBlocks(content: string): Block[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block ``` … ```
    if (line.trim().startsWith('```')) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        code.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ type: 'code', lines: code });
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      blocks.push({ type: level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3', lines: [heading[2]] });
      i++;
      continue;
    }

    // A list block is every consecutive list line, whichever marker, with its
    // indentation kept: "  - child" under "- parent" is a nested list, and a
    // numbered sub-list under a bullet stays in the same block. The block's
    // own type is the first line's marker.
    //
    // Blank lines between items do not end the list (CommonMark's "loose"
    // list): "1. …", blank, "2. …" is one numbered list. Read as three lists
    // it showed 1., 1., 1. (AIWS-098). What ends it is a non-list line, or —
    // after a blank — a top-level item of the other kind (a bullet list after
    // a numbered one is a new list, as in CommonMark).
    if (LIST_LINE.test(line)) {
      const ordered = ORDERED_LINE.test(line);
      const items: string[] = [];
      while (i < lines.length) {
        if (LIST_LINE.test(lines[i])) {
          items.push(lines[i]);
          i++;
          continue;
        }
        if (lines[i].trim() !== '') break;
        let j = i;
        while (j < lines.length && lines[j].trim() === '') j++;
        const next = lines[j];
        if (
          next !== undefined &&
          LIST_LINE.test(next) &&
          (/^\s/.test(next) || ORDERED_LINE.test(next) === ordered)
        ) {
          i = j;
          continue;
        }
        break;
      }
      blocks.push({ type: ORDERED_LINE.test(line) ? 'ol' : 'ul', lines: items });
      continue;
    }

    // Paragraph: accumulate consecutive non-blank lines.
    if (line.trim() === '') {
      i++;
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,3})\s+/.test(lines[i]) &&
      !LIST_LINE.test(lines[i]) &&
      !lines[i].trim().startsWith('```')
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'p', lines: para });
  }

  return blocks;
}

export function MarkdownView({ content, className }: { content: string; className?: string }) {
  const blocks = toBlocks(content);

  return (
    // `break-words` (inherited by every block below) is what keeps a pasted
    // URL inside the bubble: the default `overflow-wrap: normal` refuses to
    // split a "word", and a 300-character Google Maps link is one word, so it
    // ran straight through the comment's right border and off the page.
    <div className={`break-words text-[14px] leading-relaxed text-gray-700 ${className ?? ''}`}>
      {blocks.map((b, bi) => {
        const key = `b-${bi}`;
        switch (b.type) {
          case 'h1':
            return (
              <h1 key={key} className="mb-2 mt-4 text-[20px] font-semibold text-gray-800">
                {renderInline(b.lines[0], key)}
              </h1>
            );
          case 'h2':
            return (
              <h2 key={key} className="mb-2 mt-4 text-[17px] font-semibold text-gray-800">
                {renderInline(b.lines[0], key)}
              </h2>
            );
          case 'h3':
            return (
              <h3 key={key} className="mb-1 mt-3 text-[15px] font-semibold text-gray-800">
                {renderInline(b.lines[0], key)}
              </h3>
            );
          case 'code':
            return (
              <pre
                key={key}
                className="my-2 overflow-x-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-[12.5px] text-gray-700"
              >
                <code>{b.lines.join('\n')}</code>
              </pre>
            );
          case 'ul':
          case 'ol':
            return renderList(parseList(b.lines), key);
          default:
            return (
              <p key={key} className="my-1.5">
                {b.lines.map((line, i) => (
                  <Fragment key={`${key}-${i}`}>
                    {i > 0 && <br />}
                    {renderInline(line, `${key}-${i}`)}
                  </Fragment>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
}
